import React, { createContext, useContext, useEffect, useState } from 'react'
import { User, UserRole } from '@/types'
import { authService } from '../services/authService'
import toast from 'react-hot-toast'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (userData: RegisterData) => Promise<void>
  logout: () => Promise<void>
  updateUser: (userData: Partial<User>) => Promise<void>
  hasRole: (roles: UserRole[]) => boolean
  refreshToken: () => Promise<void>
}

interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  company?: string
  phone?: string
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('access_token')
        const refreshToken = localStorage.getItem('refresh_token')
        
        // Only try to authenticate if we have both tokens
        if (token && refreshToken && authService.isAuthenticated()) {
          const currentUser = await authService.getCurrentUser()
          setUser(currentUser)
        } else {
          // Clear any incomplete token data
          localStorage.removeItem('access_token')
          localStorage.removeItem('refresh_token')
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error)
        // Clear invalid tokens
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()
  }, [])

  // Set up axios interceptor for token refresh
  useEffect(() => {
    const interceptor = authService.setupTokenRefresh(async () => {
      try {
        await refreshToken()
      } catch (error) {
        console.error('Token refresh failed:', error)
        await logout()
      }
    })

    return interceptor
  }, [])

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true)
      const response = await authService.login(email, password)
      setUser(response.user)
      toast.success(`Bienvenido, ${response.user.firstName}`)
    } catch (error: any) {
      console.error('Login error:', error)
      const message = error.response?.data?.message || 'Error al iniciar sesión'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: RegisterData) => {
    try {
      setIsLoading(true)
      const response = await authService.register(userData)
      setUser(response.user)
      toast.success(`Bienvenido, ${response.user.firstName}! Tu cuenta ha sido creada.`)
    } catch (error: any) {
      console.error('Registration error:', error)
      const message = error.response?.data?.message || 'Error en el registro'
      toast.error(message)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
      toast.success('Sesión cerrada correctamente')
    } catch (error) {
      console.error('Logout error:', error)
      // Force logout even if request fails
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
    }
  }

  const updateUser = async (userData: Partial<User>) => {
    try {
      const updatedUser = await authService.updateProfile(userData)
      setUser(updatedUser)
      toast.success('Perfil actualizado correctamente')
    } catch (error: any) {
      console.error('Update user error:', error)
      const message = error.response?.data?.message || 'Error al actualizar perfil'
      toast.error(message)
      throw error
    }
  }

  const refreshToken = async () => {
    try {
      const refreshTokenValue = localStorage.getItem('refresh_token')
      if (!refreshTokenValue) {
        throw new Error('No refresh token available')
      }
      
      await authService.refreshToken()
      // Get updated user data after token refresh
      const currentUser = await authService.getCurrentUser()
      setUser(currentUser)
    } catch (error) {
      console.error('Token refresh error:', error)
      // Clear tokens on refresh failure
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      setUser(null)
      throw error
    }
  }

  const hasRole = (roles: UserRole[]): boolean => {
    return user ? roles.includes(user.role) : false
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateUser,
    hasRole,
    refreshToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
